import { useId, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Connection } from "@rago/web-shared";
import {
  acceptConnection,
  connectionsKey,
  deleteConnection,
  fetchConnections,
  invalidateSharing,
  libraryCounts,
  narrowShareLine,
  personSubLine,
  requestLine,
  requestSentence,
  shareColumn,
  splitConnections,
  type LibraryCounts,
  type ShareColumn,
} from "../../../lib/sharing";
import { useServers } from "../../../lib/servers";
import { useSources } from "../../../sources";
import { useNavServers } from "../../../lib/navServers";
import { Chip, ErrorNote, InitialBadge, Spinner } from "../../../components/ui";
import {
  SheetCard,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetSection,
} from "../../../components/settings-sheet";
import { ChevronRightIcon, PlusIcon } from "../../../components/icons";
import { ShareWizard } from "../../../components/share-wizard";

export const Route = createFileRoute("/_authed/settings/sharing")({
  component: Sharing,
});

export const SHARING_LEDE =
  "Sharing connects your Rago account with someone else's. They accept once; after that either of you can share libraries from the servers you own, and change it any time. Shared users can browse and play what you share — never your server's settings.";

/**
 * Settings › Sharing (Kenny's Claude Design "Sharing 1a", in the app's own
 * accent): the requests to connect that wait on this account, then
 * everyone it is connected with — one row per PERSON with a column per
 * direction (what I share with them, what they share with me) and the
 * invites I sent that still wait. A row is one link to the person's page,
 * where everything about them is changed; nothing is edited here.
 */
function Sharing() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { servers } = useServers();
  const ownsAny = servers.some((s) => s.owned);
  const { sources } = useSources();
  const nav = useNavServers(sources);
  const connections = useQuery({ queryKey: connectionsKey, queryFn: fetchConnections });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const requestsId = useId();

  const { requests, people } = splitConnections(connections.data ?? []);
  // An all-libraries share counts through its server's own list.
  const counts = libraryCounts(nav.data, servers);

  async function answer(c: Connection, accept: boolean) {
    setBusyId(c.id);
    setError(null);
    try {
      if (accept) {
        await acceptConnection(c.id);
        // Their grants exist now, so the server list changes too. Then
        // straight to their page: the next thing to do is pick libraries.
        await invalidateSharing(qc, { resources: true });
        await navigate({ to: "/settings/sharing/$userId", params: { userId: c.user.userId } });
      } else {
        await deleteConnection(c.id);
        await invalidateSharing(qc);
      }
    } catch (err) {
      setError(err);
      await invalidateSharing(qc);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="@container">
      <SheetHeader
        title="Sharing"
        lede={SHARING_LEDE}
        // The title's row, not `action`: the lede keeps the full width on a phone.
        status={
          ownsAny ? (
            <button type="button" className="rago-btn rago-btn-md" onClick={() => setWizardOpen(true)}>
              <PlusIcon size={15} />
              Grant access
            </button>
          ) : undefined
        }
      />
      {error ? (
        <SheetInset>
          <ErrorNote error={error} />
        </SheetInset>
      ) : null}
      {connections.isLoading ? (
        <SheetLoading />
      ) : (
        <SheetSection>
          <div className="flex flex-col gap-8">
            {requests.length > 0 ? (
              <section aria-labelledby={requestsId}>
                <h2 id={requestsId} className="rago-caps mb-3">
                  Wants to connect
                </h2>
                <ul className="flex flex-col gap-3">
                  {requests.map((c) => (
                    <li key={c.id}>
                      <RequestCard
                        connection={c}
                        counts={counts}
                        busy={busyId === c.id}
                        onAccept={() => void answer(c, true)}
                        onDecline={() => void answer(c, false)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            <People people={people} counts={counts} ownsAny={ownsAny} />
          </div>
        </SheetSection>
      )}

      <ShareWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        connections={connections.data ?? []}
      />
    </div>
  );
}

/**
 * Somebody asked to connect: who, when, what they offer, and the answer.
 * Raised over the list's card — it is the one thing here that waits on me.
 * `plain`: on the person's own page, where their name and when they asked
 * are already above it — only the sentence and the answer.
 */
export function RequestCard(props: {
  connection: Connection;
  counts: LibraryCounts;
  busy: boolean;
  plain?: boolean;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const c = props.connection;
  const name = c.user.displayName || c.user.username;
  return (
    <SheetCard raised className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3.5">
      {props.plain ? (
        <p className="min-w-0 flex-1 basis-56 text-sm leading-snug text-[var(--rago-text-2)]">
          {requestSentence(c, props.counts)}
        </p>
      ) : (
        <>
          <InitialBadge name={name} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] leading-tight font-semibold">{name}</p>
            <p className="mt-1.5 truncate text-[13px] leading-tight text-[var(--rago-text-dim)] @max-[720px]:leading-snug @max-[720px]:whitespace-normal">
              {requestLine(c, props.counts)}
            </p>
          </div>
        </>
      )}
      {/* Narrow: the answer takes its own line, under the words (past the avatar). */}
      <div
        className={`flex shrink-0 gap-3 ${
          props.plain ? "" : "@max-[720px]:basis-full @max-[720px]:pl-14"
        }`}
      >
        <button
          type="button"
          className="rago-btn rago-btn-quiet rago-btn-sm"
          onClick={props.onDecline}
          disabled={props.busy}
        >
          Decline
        </button>
        <button
          type="button"
          className="rago-btn rago-btn-sm"
          onClick={props.onAccept}
          disabled={props.busy}
        >
          {props.busy ? <Spinner size={13} /> : null}
          Accept
        </button>
      </div>
    </SheetCard>
  );
}

/** The two share columns are 160px each and the chevron 18: the label row
 * above the card repeats the row's own boxes so the words line up. */
const COLUMN = "w-40 shrink-0 @max-[720px]:hidden";

function People(props: { people: Connection[]; counts: LibraryCounts; ownsAny: boolean }) {
  const id = useId();
  const { people } = props;
  return (
    <section aria-labelledby={id}>
      <div className="mb-3 flex items-center gap-4 pr-[18px] pl-4">
        <h2 id={id} className="rago-caps flex-1">
          People{people.length > 0 ? ` · ${people.length}` : ""}
        </h2>
        {people.length > 0 ? (
          <>
            <span aria-hidden className={`rago-caps ${COLUMN}`}>
              You share
            </span>
            <span aria-hidden className={`rago-caps ${COLUMN}`}>
              They share with you
            </span>
            <span aria-hidden className="w-[18px] shrink-0 @max-[720px]:hidden" />
          </>
        ) : null}
      </div>
      <SheetCard>
        {people.length === 0 ? (
          <p className="p-6 text-[14.5px] leading-normal text-[var(--rago-text-3)]">
            {props.ownsAny
              ? "No one yet. Use Grant access to invite someone with a Rago account."
              : "No one yet. When someone shares with you, their request shows up here."}
          </p>
        ) : (
          <ul>
            {people.map((c) => (
              <PersonRow key={c.id} connection={c} counts={props.counts} />
            ))}
          </ul>
        )}
      </SheetCard>
    </section>
  );
}

/** One person, one link: who they are and since when, then what goes each way. */
function PersonRow(props: { connection: Connection; counts: LibraryCounts }) {
  const c = props.connection;
  const name = c.user.displayName || c.user.username;
  const pending = c.status === "pending";
  const sub = "mt-1.5 truncate text-[13px] leading-tight text-[var(--rago-text-dim)]";
  return (
    <li className="border-t border-[var(--rago-line-1)] first:border-t-0">
      <Link
        to="/settings/sharing/$userId"
        params={{ userId: c.user.userId }}
        className="group flex items-center gap-4 py-3.5 pr-[18px] pl-4 text-inherit no-underline transition-colors hover:bg-white/[0.03] focus-visible:shadow-[inset_0_0_0_2px_var(--rago-ring)] focus-visible:outline-none"
      >
        <InitialBadge name={name} dashed={pending} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[15px] leading-tight font-semibold">{name}</span>
            {pending ? <Chip tone="muted">Invite sent</Chip> : null}
          </span>
          <span className={`block @max-[720px]:hidden ${sub}`}>{personSubLine(c)}</span>
          {/* Too narrow for the columns: both directions take the sub-line. */}
          <span className="mt-1.5 hidden text-[13px] leading-snug text-[var(--rago-text-dim)] @max-[720px]:block">
            {narrowShareLine(c, props.counts)}
          </span>
        </span>
        <ShareCell label="You share" column={shareColumn(c.given, props.counts, "given", pending)} />
        <ShareCell
          label="They share with you"
          column={shareColumn(c.received, props.counts, "received", pending)}
        />
        <ChevronRightIcon
          size={18}
          className="shrink-0 text-[var(--rago-text-dim)] transition-colors group-hover:text-[var(--rago-text-1)]"
        />
      </Link>
    </li>
  );
}

function ShareCell(props: { label: string; column: ShareColumn }) {
  const { column } = props;
  return (
    <span className={COLUMN}>
      {/* The column's heading sits outside the link: say it with the value. */}
      <span className="sr-only">{props.label}: </span>
      <span
        className={`block truncate text-sm leading-tight ${
          column.quiet ? "text-[var(--rago-text-dim)]" : "font-semibold text-[var(--rago-text-2)]"
        }`}
      >
        {column.primary}
      </span>
      {column.secondary ? (
        <span className="mt-[5px] block truncate text-[12.5px] leading-tight text-[var(--rago-text-dim)]">
          {column.secondary}
        </span>
      ) : null}
    </span>
  );
}
